import { useEffect, useRef } from 'react';
import { micSelectorHost } from '../../../configure/host-inputs-facade';
import { useAudioSystem } from '../../../useAudioSystem';
import { BarsBase } from './BarsBase';
import { setBarHeight } from './barTransform';

interface Props {
  isSilent?: boolean;
  /**
   * Force the destructive (red) bar colour regardless of the web audio graph's
   * readiness. Used for the desktop silent-capture warnings, where
   * detection is native (Rust) — `isSilent` alone never paints red on desktop
   * because `audioReady` tracks the browser audio system, which is idle there.
   */
  isError?: boolean;
  className?: string;
  size?: 'default' | 'compact';
  barCount?: 3 | 5;
}

export const DesktopBars = ({
  isSilent,
  isError,
  className,
  size = 'default',
  barCount = 5,
}: Props) => {
  const barsRef = useRef<HTMLDivElement[]>([]);
  const { isReady, errors } = useAudioSystem();
  const audioReady = isReady && !errors.length;
  const heights = micSelectorHost.useNativeAudioLevels();
  const minHeight = size === 'compact' ? 3 : 7.5;
  const heightMultiplier = size === 'compact' ? 0.5 : 1;
  // With 3 bars, render the centre three of the 5 level bands so the
  // mid-tall-mid arc and per-band responsiveness are preserved.
  const levelOffset = barCount === 3 ? 1 : 0;

  useEffect(() => {
    if (!heights || heights.length !== 5) return;

    const rafId = requestAnimationFrame(() => {
      barsRef.current.forEach((bar, index) => {
        if (!bar) return;
        const height = Math.max(
          (heights[index + levelOffset] ?? 0) * heightMultiplier,
          minHeight
        );
        setBarHeight(bar, height, size);
      });
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [heights, minHeight, heightMultiplier, levelOffset, size]);

  return (
    <BarsBase
      barsRef={barsRef}
      isSilent={isSilent}
      isError={isError}
      audioReady={audioReady}
      size={size}
      barCount={barCount}
      className={className}
    />
  );
};
